/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { BackupRecord, User } from '../types';
import { 
  X, 
  Database, 
  Download, 
  RefreshCw, 
  Trash2, 
  Clock, 
  Plus, 
  ShieldCheck, 
  AlertTriangle, 
  Lock, 
  CheckCircle2, 
  Layers, 
  FileText, 
  Users, 
  ShieldAlert,
  Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { BackupService } from '../services/backupService';

interface BackupManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  backups: BackupRecord[];
  currentUser: User | null;
  onGenerateManualBackup: () => void;
  onRestoreCompleted?: () => void;
  onDeleteBackup: (backupId: string) => void;
}

type RestoreFlowState = 
  | 'IDLE' 
  | 'DRY_RUN_LOADING' 
  | 'AWAITING_CONFIRMATION' 
  | 'RESTORE_LOADING' 
  | 'SUCCESS' 
  | 'ERROR';

export default function BackupManagerModal({
  isOpen,
  onClose,
  backups,
  currentUser,
  onGenerateManualBackup,
  onRestoreCompleted,
  onDeleteBackup
}: BackupManagerModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Server-Side Dry-Run and Execute Restore State Machine
  const [restoreState, setRestoreState] = useState<RestoreFlowState>('IDLE');
  const [targetBackup, setTargetBackup] = useState<BackupRecord | null>(null);
  const [dryRunData, setDryRunData] = useState<any | null>(null);
  const [correlationId, setCorrelationId] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [restoreResultData, setRestoreResultData] = useState<any | null>(null);

  if (!isOpen) return null;

  // Filter backups based on active company
  const activeCompanyId = currentUser?.companyId || 'company-insumedic';
  const companyFilteredBackups = backups.filter(b => {
    const backupCId = b.companyId || 'company-insumedic';
    if (activeCompanyId.startsWith('company-insumedic') && backupCId.startsWith('company-insumedic')) {
      return true;
    }
    return backupCId === activeCompanyId;
  });

  const filteredBackups = companyFilteredBackups.filter(b => 
    b.fechaHora.includes(searchTerm) || 
    b.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDownloadJson = (backup: BackupRecord) => {
    const dataStr = JSON.stringify(backup, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `insumedic_backup_${backup.tipo.replace(/[\s\(\):]/g, '_')}_${backup.fechaHora.replace(/[\s:-]/g, '')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setSuccessMsg('¡Archivo JSON de Copia de Seguridad descargado exitosamente!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleStartRestoreDryRun = async (backup: BackupRecord) => {
    setTargetBackup(backup);
    setRestoreState('DRY_RUN_LOADING');
    setDryRunData(null);
    setErrorCode('');
    setErrorMessage('');
    setRestoreResultData(null);

    const result = await BackupService.dryRunServerRestore(backup, currentUser);

    setCorrelationId(result.correlationId);

    if (!result.success) {
      setRestoreState('ERROR');
      setErrorCode(result.errorCode || 'DRY_RUN_FAILED');
      setErrorMessage(result.error || 'La validación previa server-side no fue satisfactoria.');
      return;
    }

    setDryRunData(result.data);
    setRestoreState('AWAITING_CONFIRMATION');
  };

  const handleConfirmAndExecuteRestore = async () => {
    if (!targetBackup) return;

    setRestoreState('RESTORE_LOADING');

    const result = await BackupService.executeServerRestore(
      targetBackup,
      currentUser,
      'Restauración autorizada tras validación Server-Side Dry-Run'
    );

    setCorrelationId(result.correlationId);

    if (!result.success) {
      setRestoreState('ERROR');
      setErrorCode(result.errorCode || 'RESTORE_FAILED');
      setErrorMessage(result.error || 'La ejecución de la restauración en el servidor ha fallado.');
      return;
    }

    setRestoreResultData(result.data);
    setRestoreState('SUCCESS');

    // Trigger data reload from server
    if (onRestoreCompleted) {
      onRestoreCompleted();
    }
  };

  const resetRestoreState = () => {
    setRestoreState('IDLE');
    setTargetBackup(null);
    setDryRunData(null);
    setErrorCode('');
    setErrorMessage('');
    setRestoreResultData(null);
  };

  const executeGenerate = () => {
    onGenerateManualBackup();
    setSuccessMsg('¡Se ha creado una nueva Copia de Seguridad Manual e inalterable en la nube!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-3xl border border-slate-200/90 shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]"
        id="backup-manager-container"
      >
        {/* Modal Header */}
        <div className="p-6 bg-slate-900 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sky-500/20 text-sky-400 rounded-xl border border-sky-500/30">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-sans font-black tracking-wide uppercase leading-tight">
                Gestor de Copias de Seguridad (Audit)
              </h2>
              <p className="text-[10px] text-slate-400 font-mono mt-0.5 uppercase tracking-wider">
                Garantía de Resguardo & Protección Inalterable a las 18:00
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Info Strip (Automated daily trigger reminder) */}
        <div className="bg-sky-50 border-b border-sky-100 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shrink-0 font-sans">
          <div className="flex items-center gap-3">
            <div className="relative flex shrink-0 h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </div>
            <div className="text-xs">
              <span className="font-extrabold text-slate-800 uppercase block leading-none">PROGRAMADOR DIARIO ACTIVO</span>
              <span className="text-[10px] text-slate-500 mt-0.5 block">
                Copia diaria programada para ejecutarse automáticamente todos los días a las <strong className="font-bold text-slate-700">18:00 horas</strong>.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={executeGenerate}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer shadow-3xs"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Copia Manual Ahora</span>
            </button>
          </div>
        </div>

        {/* Main Content Scrollable Area */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 min-h-0 bg-slate-50">
          
          {/* Status feedback alerts */}
          <AnimatePresence>
            {successMsg && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-3 bg-emerald-50 border border-emerald-250 text-emerald-800 rounded-xl text-xs font-sans font-bold flex items-center gap-2"
              >
                <ShieldCheck className="h-4 w-4 text-emerald-650 shrink-0" />
                <span>{successMsg}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search bar */}
          <div className="relative shrink-0">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar copias de seguridad por fecha, tipo o identificador..."
              className="w-full text-xs font-sans pl-3 pr-10 py-2 border border-slate-250 rounded-xl focus:outline-hidden focus:ring-1 focus:ring-sky-500 focus:border-sky-500 bg-white"
            />
            <div className="absolute right-3 top-2.5 text-slate-400">
              <Clock className="h-4 w-4" />
            </div>
          </div>

          {/* Backups List */}
          <div className="space-y-3">
            <span className="block text-[11px] font-sans font-black text-slate-800 uppercase tracking-wider">Historial de Resguardos Recientes</span>
            
            {filteredBackups.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-2">
                <Database className="h-8 w-8 text-slate-300 mx-auto" />
                <p className="text-xs text-slate-500 font-sans font-medium">No se encontraron copias de seguridad de datos registradas en este entorno.</p>
                <p className="text-[10px] text-slate-400 font-mono">El programador diario creará el primer registro hoy a las 18:00 de forma desasistida.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-xs">
                {filteredBackups.map((backup) => (
                  <div key={backup.id} className="p-4 hover:bg-slate-50/60 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-sans">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase ${
                          backup.tipo.startsWith('AUTOMATICA') 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' 
                            : 'bg-indigo-50 text-indigo-700 border border-indigo-150'
                        }`}>
                          {backup.tipo}
                        </span>
                        <span className="font-mono text-[10px] text-slate-400 font-bold">{backup.id}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span>Resguardado en: <strong className="font-black text-slate-800">{backup.fechaHora}</strong></span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-sans">
                        Contenido: <strong className="text-slate-700">{backup.ordenesCount} órdenes</strong>, <strong className="text-slate-700">{backup.disposicionesCount} liberaciones</strong>, <strong className="text-slate-700">{backup.usersCount} usuarios</strong>.
                      </div>
                      {backup.hashSha256 && (
                        <div className="text-[9px] font-mono text-emerald-700 flex items-center gap-1 mt-0.5" title={`Hash SHA-256: ${backup.hashSha256}`}>
                          <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
                          <span>SHA-256: {backup.hashSha256.substring(0, 12)}... (Inalterable)</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <button
                        onClick={() => handleDownloadJson(backup)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-bold rounded-lg transition-colors cursor-pointer"
                        title="Descargar archivo en formato JSON inalterable de auditoría"
                      >
                        <Download className="h-3 w-3" />
                        <span>Descargar JSON</span>
                      </button>
                      <button
                        onClick={() => handleStartRestoreDryRun(backup)}
                        disabled={restoreState === 'DRY_RUN_LOADING' || restoreState === 'RESTORE_LOADING'}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-900 hover:bg-slate-950 disabled:bg-slate-400 text-white font-bold rounded-lg transition-colors cursor-pointer shadow-3xs"
                        title="Restaurar toda la base de datos a este punto específico del tiempo (Server-Side)"
                      >
                        {restoreState === 'DRY_RUN_LOADING' && targetBackup?.id === backup.id ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin text-sky-400" />
                            <span>Validando...</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3 w-3" />
                            <span>Restaurar</span>
                          </>
                        )}
                      </button>
                      {currentUser?.username?.toUpperCase() === 'JUAN.VALENCIA' ? (
                        <button
                          onClick={() => onDeleteBackup(backup.id)}
                          className="p-1.5 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-rose-100"
                          title="Remover copia permanentemente (SuperAdmin)"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="p-1.5 text-slate-300 cursor-not-allowed" title="Inmutable: Solo el SuperAdministrador puede purgar resguardos">
                          <Lock className="h-3.5 w-3.5 text-slate-300" />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-between items-center shrink-0 text-[10px] text-slate-400 font-mono font-bold uppercase tracking-wider">
          <span>Insumedic Data Security Vault</span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-sans font-bold text-xs rounded-xl cursor-pointer transition-colors"
          >
            Cerrar Ventana
          </button>
        </div>
      </motion.div>

      {/* OVERLAY: VALIDACIÓN PREVIA A RESTAURACIÓN (DRY-RUN CONFIRMATION MODAL) */}
      <AnimatePresence>
        {(restoreState === 'AWAITING_CONFIRMATION' || restoreState === 'RESTORE_LOADING') && dryRunData && targetBackup && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-7 max-w-lg w-full space-y-5"
            >
              {/* Header */}
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl border border-amber-200">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
                    Validación Previa a Restauración
                  </h3>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                    VERIFICACIÓN SERVER-SIDE DRY-RUN
                  </p>
                </div>
                <div className="ml-auto">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded font-mono text-[9px] font-bold">
                    {correlationId}
                  </span>
                </div>
              </div>

              {/* Pre-Restore Verification Checklist */}
              <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 space-y-2 text-xs">
                <span className="block text-[10px] font-black uppercase text-slate-500 font-mono tracking-wider mb-2">
                  Checklist de Seguridad Criptográfica
                </span>

                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Usuario: <strong className="font-semibold">{currentUser?.nombreCompleto || currentUser?.username}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Rol: <strong className="font-semibold">{currentUser?.cargo || 'Director Técnico'}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Tenant: <strong className="font-semibold font-mono">{dryRunData.tenantId}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Backup ID: <strong className="font-semibold font-mono">{dryRunData.backupId?.substring(0, 14)}...</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700 col-span-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>SHA-256 íntegro: <strong className="font-mono text-emerald-700">{dryRunData.verifiedSha256?.substring(0, 20)}...</strong></span>
                  </div>
                </div>

                <div className="border-t border-slate-200/80 pt-2 mt-2 grid grid-cols-3 gap-2 text-[10px] text-center font-mono">
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Órdenes</span>
                    <strong className="text-slate-800 text-xs">{dryRunData.ordenesCount}</strong>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Disposiciones</span>
                    <strong className="text-slate-800 text-xs">{dryRunData.disposicionesCount}</strong>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Conflictos</span>
                    <strong className="text-emerald-600 text-xs">{dryRunData.conflictsCount ?? 0}</strong>
                  </div>
                </div>
              </div>

              {/* Warning Notice */}
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-black uppercase text-[11px] text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  <span>Advertencia de Sobrescritura</span>
                </div>
                <p className="text-[11px] leading-relaxed text-amber-800">
                  Esta operación reemplazará la información actual del tenant por la información contenida en el resguardo seleccionado ({dryRunData.backupTimestamp || targetBackup.fechaHora}).
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2.5 pt-1">
                <button
                  type="button"
                  disabled={restoreState === 'RESTORE_LOADING'}
                  onClick={resetRestoreState}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  CANCELAR
                </button>
                <button
                  type="button"
                  disabled={restoreState === 'RESTORE_LOADING'}
                  onClick={handleConfirmAndExecuteRestore}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white rounded-xl text-xs font-black uppercase tracking-wider cursor-pointer transition-all shadow-md"
                >
                  {restoreState === 'RESTORE_LOADING' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Restaurando en Servidor...</span>
                    </>
                  ) : (
                    <span>CONTINUAR CON RESTAURACIÓN</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* OVERLAY: RESTORE ERROR MODAL */}
      <AnimatePresence>
        {restoreState === 'ERROR' && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-rose-200 shadow-2xl p-6 sm:p-7 max-w-md w-full space-y-4"
            >
              <div className="flex items-center gap-3 border-b border-rose-100 pb-3">
                <div className="p-2 bg-rose-100 text-rose-600 rounded-xl">
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-rose-950 uppercase">
                    Fallo en la Validación / Restauración
                  </h3>
                  <p className="text-[10px] text-rose-600 font-mono">
                    ERROR DE OPERACIÓN SERVER-SIDE
                  </p>
                </div>
              </div>

              <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl space-y-1.5 text-xs text-rose-900">
                <div className="flex items-center justify-between">
                  <span className="font-bold uppercase text-[10px] text-rose-700">Código de Error:</span>
                  <span className="font-mono font-bold text-rose-800 bg-rose-100 px-1.5 py-0.5 rounded text-[10px]">
                    {errorCode}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed pt-1">
                  {errorMessage}
                </p>
                {correlationId && (
                  <div className="pt-2 border-t border-rose-200/70 text-[10px] font-mono text-rose-600 flex items-center justify-between">
                    <span>Correlation ID:</span>
                    <span>{correlationId}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={resetRestoreState}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-950 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* OVERLAY: RESTORE SUCCESS MODAL */}
      <AnimatePresence>
        {restoreState === 'SUCCESS' && restoreResultData && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-emerald-200 shadow-2xl p-6 sm:p-7 max-w-md w-full space-y-4"
            >
              <div className="flex items-center gap-3 border-b border-emerald-100 pb-3">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-emerald-950 uppercase">
                    ¡Restauración Server-Side Exitosa!
                  </h3>
                  <p className="text-[10px] text-emerald-600 font-mono">
                    TRANSACCIÓN ATÓMICA CONFIRMADA
                  </p>
                </div>
              </div>

              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2 text-xs text-emerald-900">
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <span className="text-emerald-700 text-[10px] block">Órdenes Restauradas:</span>
                    <strong className="text-sm text-emerald-950">{restoreResultData.restoredOrdenesCount}</strong>
                  </div>
                  <div>
                    <span className="text-emerald-700 text-[10px] block">Disposiciones:</span>
                    <strong className="text-sm text-emerald-950">{restoreResultData.restoredDisposicionesCount}</strong>
                  </div>
                </div>
                <div className="pt-2 border-t border-emerald-200 text-[10px] font-mono text-emerald-700">
                  <span>SHA-256 Verificado: {restoreResultData.verifiedSha256?.substring(0, 20)}...</span>
                </div>
                {correlationId && (
                  <div className="text-[9px] font-mono text-emerald-600">
                    <span>Audit ID: {correlationId}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={resetRestoreState}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors shadow-sm"
                >
                  Aceptar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
